import { type ReactNode } from 'react';
import './FlipCard.css';

export interface FlipCardProps {
  /** Face shown before the flip (the prompt). */
  front: ReactNode;
  /** Face revealed after the flip (the answer). */
  back: ReactNode;
  /** Controlled flip state. Flipping is one-way per card (Anki-style). */
  flipped: boolean;
  /** Requested when the user taps the unflipped card. */
  onFlip?: () => void;
  /** Accessible label for the tappable card, e.g. "გადაატრიალე ბარათი". */
  flipLabel: string;
  disabled?: boolean;
}

/**
 * FlipCard — a tap-to-flip study card with a 3D Y-axis rotation.
 *
 * The root element NEVER changes across flips (a root swap would remount the
 * subtree and CSS transitions never fire on freshly-inserted nodes — the flip
 * would pop instead of rotate). The whole-card tap target is a transparent
 * overlay button that unmounts after the flip; reveals are one-way like Anki.
 * The hidden face is aria-hidden in both directions — `backface-visibility`
 * is paint-only and does not remove content from the accessibility tree.
 * Under prefers-reduced-motion the rotation collapses to an instant swap.
 */
export function FlipCard({ front, back, flipped, onFlip, flipLabel, disabled }: FlipCardProps) {
  return (
    <div className="fluent-flipcard" data-flipped={flipped ? 'true' : 'false'}>
      <span className="fluent-flipcard__inner">
        <span className="fluent-flipcard__face fluent-flipcard__face--front" aria-hidden={flipped}>
          {front}
        </span>
        <span className="fluent-flipcard__face fluent-flipcard__face--back" aria-hidden={!flipped}>
          {back}
        </span>
      </span>
      {!flipped && (
        <button
          type="button"
          className="fluent-flipcard__tap"
          onClick={onFlip}
          disabled={disabled}
          aria-label={flipLabel}
        />
      )}
    </div>
  );
}
